'use client';

// Hooks that surface user-preference state from the network store.
//
// For SDK / RPC / banner / NetworkLink decisions, do NOT use this file —
// use `useNetworkMode()` from `./use-network-mode.ts` instead. That hook
// is gate-clamped (returns `'devnet'` whenever `mainnet_enabled` is
// off) and is the single source of truth for "which network is the
// user on?".
//
// The hook below (`useUserNetwork`) returns the RAW preference value —
// it doesn't apply the gate. Use it only for the wallet-toggle UI's
// "currently checked" state and for anything that needs to know the
// raw cookie value (e.g. share-button URL builders).

import { clearRelayFeePayerCache } from '@morfi/backend-sdk-v2/client';
import { teardownImpersonation } from '@/lib/impersonation/teardown';

import { isToggleEnabled } from './config';
import { useNetworkStore } from './store';
import type { Network } from './types';

export interface UseUserNetworkResult {
  /** Raw preference — can be `'mainnet'` even when the gate is off.
   *  Hydrates from the `NETWORK_PREFERENCE` cookie on mount. */
  network: Network;
  /** Persists the preference to the cookie + Zustand store. Awaits any
   *  active engineer-impersonation teardown FIRST so the grant token
   *  is revoked / cleared before the SDK re-binds against the new
   *  network (FR-029, FR-068 — grants are per-network and must not
   *  survive a toggle). The caller is responsible for any SDK re-init
   *  or URL change that needs to happen on toggle (the wallet toggle
   *  uses `useSwitchNetwork()` which wraps this and adds the URL
   *  flip). */
  setUserNetwork: (next: Network) => Promise<void>;
  /** True when the deployment ships both network configs. The toggle
   *  UI is hidden when false — only one network is reachable. */
  toggleEnabled: boolean;
}

export function useUserNetwork(): UseUserNetworkResult {
  const network = useNetworkStore((s): Network => s.current);
  const storeSetUserNetwork = useNetworkStore((s): ((next: Network) => void) => s.setUserNetwork);

  async function setUserNetwork(next: Network): Promise<void> {
    // Tear down any active engineer impersonation BEFORE flipping the
    // network. Grants are scoped to a single network on the backend
    // (see grant-api.md); leaving an impersonation header attached
    // when the SDK re-binds to the other network's apiUrl would
    // result in 401 chatter at best, header-on-wrong-network leakage
    // at worst. Idempotent on a non-impersonating session.
    await teardownImpersonation('network_toggle');
    // Clear the cached relay fee-payer BEFORE flipping the network so
    // the next relay submission resolves the new network's fee-payer
    // (T116 — fee-payer is per-network).
    clearRelayFeePayerCache();
    storeSetUserNetwork(next);
  }

  return { network, setUserNetwork, toggleEnabled: isToggleEnabled() };
}
